import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import { usePermission } from '@/hooks/usePermission'
import { getSubscriptionStatus, getSubscriptionUsage, getLockedResources } from '@/services/api/subscription.service'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import { useSubscriptionStore } from '@/stores/subscription.store'
import type { SubscriptionStatus } from '@/types/enums'

export function useSubscription() {
  // Select stable actions: depending on the whole store would re-run the effects on every write.
  const subscriptionStatus = useSubscriptionStore((s) => s.status)
  const setStatus = useSubscriptionStore((s) => s.setStatus)
  const setCancelAtPeriodEnd = useSubscriptionStore((s) => s.setCancelAtPeriodEnd)
  const setCurrentPeriodEnd = useSubscriptionStore((s) => s.setCurrentPeriodEnd)
  const setLockedResourceIds = useSubscriptionStore((s) => s.setLockedResourceIds)
  const queryClient = useQueryClient()
  // Members without subscription:view would only get 403s.
  const { can, isLoading: permissionsLoading } = usePermission()
  const canView = !permissionsLoading && can(PERMISSION_KEYS.SUBSCRIPTION_VIEW)

  const statusQuery = useQuery({
    queryKey: ['subscription', 'status'],
    queryFn: getSubscriptionStatus,
    staleTime: 60 * 1000,
    enabled: canView,
  })

  const usageQuery = useQuery({
    queryKey: ['subscription', 'usage'],
    queryFn: getSubscriptionUsage,
    staleTime: 60 * 1000,
    enabled: canView,
  })

  const lockedResourcesQuery = useQuery({
    queryKey: ['subscription', 'locked-resources'],
    queryFn: getLockedResources,
    staleTime: 60 * 1000,
    enabled: canView && !!subscriptionStatus,
  })

  useEffect(() => {
    if (statusQuery.data) {
      setStatus(statusQuery.data.status as SubscriptionStatus)
      setCancelAtPeriodEnd(statusQuery.data.cancelAtPeriodEnd)
      if (statusQuery.data.currentPeriodEnd) {
        setCurrentPeriodEnd(new Date(statusQuery.data.currentPeriodEnd))
      }
    }
  }, [statusQuery.data, setStatus, setCancelAtPeriodEnd, setCurrentPeriodEnd])

  useEffect(() => {
    if (lockedResourcesQuery.data) {
      const ids = new Set(lockedResourcesQuery.data.map((r) => r.resourceId))
      setLockedResourceIds(ids)
    }
  }, [lockedResourcesQuery.data, setLockedResourceIds])

  return {
    status: statusQuery.data,
    usage: usageQuery.data,
    lockedResources: lockedResourcesQuery.data,
    isLoading: statusQuery.isLoading,
    error: statusQuery.error,
    refetch: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
    },
  }
}